import { Component, OnInit, inject, NgZone, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PhotoboothService } from '../services/photobooth.service';
import { PdfGeneratorService } from '../services/pdf-generator.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Firestore, collection, getDocs, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';

export interface FrameRow {
  deviceId: string;
  storeId: string;
  frameGroupId: string;
  frameId: string;
  themeId: string;
  printCount: number;
  imageUrl?: string | null;
}

export interface AggregatedFrameRow {
  deviceId?: string;
  storeId?: string;
  frameGroupId: string;
  frameId: string;
  themeId: string;
  printCount: number;
  imageUrl?: string | null;
  debugPath?: string;
  rank?: number;
}

@Component({
  selector: 'app-frame-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './frame-report.component.html',
  styleUrls: ['./frame-report.component.css'],
})
export class FrameReportComponent implements OnInit {
  private photoboothService = inject(PhotoboothService);
  private firestore = inject(Firestore);
  private zone = inject(NgZone);
  private pdfService = inject(PdfGeneratorService);
  private cd = inject(ChangeDetectorRef);
  private auth = inject(Auth);

  isLoading = false;
  isExporting = false;
  isPreviewModalOpen = false;
  isGeneratingPdf = false;
  loadingMessage = 'Loading Frame Report...';
  selectedDate: Date = new Date();
  currentDate: Date = new Date();
  
  // Toggles
  reportType: 'device' | 'store' | 'overall' = 'overall';

  // Raw fetched data for the month
  rawMonthlyData: FrameRow[] = [];
  
  // Aggregated data for grid
  baseAggregatedData: AggregatedFrameRow[] = [];
  gridData: AggregatedFrameRow[] = [];
  
  // Dropdown States
  showStoreFilterDropdown = false;
  showDeviceFilterDropdown = false;
  showFrameGroupFilterDropdown = false;
  
  // Frame Groups Filter
  allUniqueFrameGroups: string[] = [];
  selectedFrameGroups = new Set<string>();
  frameGroupFilterSearchText = '';

  // Stores Filter
  allUniqueStores: string[] = [];
  selectedStores = new Set<string>();
  storeFilterSearchText = '';
  
  // Devices Filter
  allUniqueDevices: string[] = [];
  selectedDevices = new Set<string>();
  deviceFilterSearchText = '';

  get isAllFrameGroupsSelected(): boolean {
    return this.selectedFrameGroups.size === this.allUniqueFrameGroups.length && this.allUniqueFrameGroups.length > 0;
  }

  get reportTitle(): string {
    return 'Frame Incentive Report';
  }

  get isAllStoresSelected(): boolean {
    return this.selectedStores.size === this.allUniqueStores.length && this.allUniqueStores.length > 0;
  }

  get isAllDevicesSelected(): boolean {
    return this.selectedDevices.size === this.allUniqueDevices.length && this.allUniqueDevices.length > 0;
  }

  get totalPrints(): number {
    return this.gridData.reduce((sum, row) => sum + (row.printCount || 0), 0);
  }

  get totalAmount(): number {
    return this.totalPrints * 0.25;
  }

  toggleFrameGroupFilter() {
    this.showFrameGroupFilterDropdown = !this.showFrameGroupFilterDropdown;
    this.showStoreFilterDropdown = false;
    this.showDeviceFilterDropdown = false;
  }

  toggleStoreFilter() {
    this.showStoreFilterDropdown = !this.showStoreFilterDropdown;
    this.showDeviceFilterDropdown = false;
    this.showFrameGroupFilterDropdown = false;
  }

  toggleDeviceFilter() {
    this.showDeviceFilterDropdown = !this.showDeviceFilterDropdown;
    this.showStoreFilterDropdown = false;
    this.showFrameGroupFilterDropdown = false;
  }

  getFilteredFrameGroups() {
    if (!this.frameGroupFilterSearchText) return this.allUniqueFrameGroups;
    const lower = this.frameGroupFilterSearchText.toLowerCase();
    return this.allUniqueFrameGroups.filter(g => g.toLowerCase().includes(lower));
  }

  getFilteredStores() {
    if (!this.storeFilterSearchText) return this.allUniqueStores;
    const lower = this.storeFilterSearchText.toLowerCase();
    return this.allUniqueStores.filter(s => s.toLowerCase().includes(lower));
  }

  getFilteredDevices() {
    if (!this.deviceFilterSearchText) return this.allUniqueDevices;
    const lower = this.deviceFilterSearchText.toLowerCase();
    return this.allUniqueDevices.filter(d => d.toLowerCase().includes(lower));
  }

  savedPreferenceFrameGroups: string[] | null = null;

  async getUserPreferences(uid: string): Promise<string[] | null> {
    try {
      const prefDoc = await getDoc(doc(this.firestore, 'users', uid, 'preferences', 'frame_report'));
      if (prefDoc.exists()) {
        const data = prefDoc.data();
        if (data && Array.isArray(data['frameGroups'])) {
          return data['frameGroups'];
        }
      }
    } catch (e) {
      console.error('Error fetching frame report preferences:', e);
    }
    return null;
  }

  async saveUserPreferences(uid: string, frameGroups: string[]) {
    try {
      await setDoc(doc(this.firestore, 'users', uid, 'preferences', 'frame_report'), { frameGroups }, { merge: true });
    } catch (e) {
      console.error('Error saving frame report preferences:', e);
    }
  }

  async saveFrameGroupPreferences() {
    const user = this.auth.currentUser;
    if (user) {
      await this.saveUserPreferences(user.uid, Array.from(this.selectedFrameGroups));
    }
  }

  toggleAllFrameGroups() {
    if (this.isAllFrameGroupsSelected) {
      this.selectedFrameGroups.clear();
    } else {
      this.allUniqueFrameGroups.forEach(g => this.selectedFrameGroups.add(g));
    }
    this.applyFiltersAndPagination();
    this.saveFrameGroupPreferences();
  }

  toggleFrameGroupSelection(group: string) {
    if (this.selectedFrameGroups.has(group)) {
      this.selectedFrameGroups.delete(group);
    } else {
      this.selectedFrameGroups.add(group);
    }
    this.applyFiltersAndPagination();
    this.saveFrameGroupPreferences();
  }

  toggleAllStores() {
    if (this.isAllStoresSelected) {
      this.selectedStores.clear();
    } else {
      this.allUniqueStores.forEach(s => this.selectedStores.add(s));
    }
    this.applyFiltersAndPagination();
  }

  toggleAllDevices() {
    if (this.isAllDevicesSelected) {
      this.selectedDevices.clear();
    } else {
      this.allUniqueDevices.forEach(d => this.selectedDevices.add(d));
    }
    this.applyFiltersAndPagination();
  }

  toggleStoreSelection(store: string) {
    if (this.selectedStores.has(store)) {
      this.selectedStores.delete(store);
    } else {
      this.selectedStores.add(store);
    }
    this.applyFiltersAndPagination();
  }

  toggleDeviceSelection(device: string) {
    if (this.selectedDevices.has(device)) {
      this.selectedDevices.delete(device);
    } else {
      this.selectedDevices.add(device);
    }
    this.applyFiltersAndPagination();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-dropdown')) {
      this.showStoreFilterDropdown = false;
      this.showDeviceFilterDropdown = false;
      this.showFrameGroupFilterDropdown = false;
    }
  }

  // Top 10 frames
  top10Frames: AggregatedFrameRow[] = [];

  // Cache to avoid refetching URLs
  imageUrlCache = new Map<string, string | null>();

  ngOnInit() {
    this.fetchReport();
  }

  // Set the month picker handler
  setMonthAndYear(normalizedMonthAndYear: Date, datepicker: any) {
    this.selectedDate = new Date(normalizedMonthAndYear.getFullYear(), normalizedMonthAndYear.getMonth(), 1);
    datepicker.close();
    this.fetchReport();
  }

  async fetchReport() {
    this.isLoading = true;
    this.loadingMessage = 'Loading Frame Report Data...';
    
    const month = this.selectedDate.getMonth() + 1;
    const year = this.selectedDate.getFullYear();
    const periodStr = `${year}-${String(month).padStart(2, '0')}`;

    this.rawMonthlyData = [];
    this.gridData = [];
    this.top10Frames = [];

    try {
      // 1. Fetch devices to get deviceId -> storeId mapping
      const devices = await this.photoboothService.getAllDevices();
      const deviceMap = new Map<string, string>(); // deviceId -> storeId
      
      const devicesRef = collection(this.firestore, 'devices');
      const devicesSnap = await getDocs(devicesRef);
      
      for (const dev of devices) {
         deviceMap.set(dev.id, dev.data?.storeId || 'Unknown Store');
      }

      // 2. Fetch frames for each device in the given month
      // Path: devices/{deviceId}/reports/{periodStr}/frames
      const promises = devicesSnap.docs.map(async (deviceDoc) => {
        const deviceId = deviceDoc.id;
        const framesRef = collection(this.firestore, `devices/${deviceId}/reports/${periodStr}/frames`);
        const framesSnap = await getDocs(framesRef);
        
        const storeId = deviceMap.get(deviceId) || 'Unknown Store';
        const frames: FrameRow[] = [];

        for (const frameDoc of framesSnap.docs) {
           const data = frameDoc.data();
           frames.push({
             deviceId: deviceId,
             storeId: storeId,
             frameGroupId: data['frameGroupId'] || '',
             frameId: data['frameId'] || frameDoc.id,
             themeId: data['themeId'] || data['tileId'] || '',
             printCount: data['printCount'] || 0,
           });
        }
        return frames;
      });

      const results = await Promise.all(promises);
      for (const res of results) {
         this.rawMonthlyData.push(...res);
      }

      // 3. Aggregate data
      await this.aggregateData();

    } catch (error) {
      console.error('Error fetching frame report:', error);
      alert('Failed to load frame report data.');
    } finally {
      this.zone.run(() => {
        this.isLoading = false;
      });
    }
  }

  async onReportTypeChange(type: 'device' | 'store' | 'overall') {
    if (this.reportType === type) return;
    this.reportType = type;
    await this.aggregateData();
  }

  async aggregateData() {
    this.isLoading = true;
    
    try {
      const map = new Map<string, AggregatedFrameRow>();

      for (const row of this.rawMonthlyData) {
        let key = '';
        if (this.reportType === 'device') {
          key = `${row.storeId}_${row.deviceId}_${row.frameGroupId}_${row.frameId}`;
        } else if (this.reportType === 'store') {
          key = `${row.storeId}_${row.frameGroupId}_${row.frameId}`;
        } else {
          key = `${row.frameGroupId}_${row.frameId}`;
        }

        if (map.has(key)) {
          const existing = map.get(key)!;
          existing.printCount += row.printCount;
          // Keep the first valid deviceId we encounter for image lookup
          if (!existing.deviceId && row.deviceId) {
            existing.deviceId = row.deviceId;
          }
        } else {
          map.set(key, {
            deviceId: row.deviceId, // Always keep a deviceId for image lookup
            storeId: (this.reportType === 'device' || this.reportType === 'store') ? row.storeId : undefined,
            frameGroupId: row.frameGroupId,
            frameId: row.frameId,
            themeId: row.themeId,
            printCount: row.printCount,
            imageUrl: null // Will fetch after
          });
        }
      }

      const aggregated = Array.from(map.values());
      // Sort desc by prints
      aggregated.sort((a, b) => b.printCount - a.printCount);
      aggregated.forEach((row, index) => {
        row.rank = index + 1;
      });

      // Extract Top 10 for Overall KPI
      if (this.reportType === 'overall') {
        this.top10Frames = aggregated.slice(0, 10);
        await this.populateImages(this.top10Frames);
      }

      this.baseAggregatedData = aggregated;

      // Extract filter options
      const stores = new Set<string>();
      const devices = new Set<string>();
      const frameGroups = new Set<string>();
      aggregated.forEach(r => {
        if (r.storeId) stores.add(r.storeId);
        if (r.deviceId) devices.add(r.deviceId);
        if (r.frameGroupId) frameGroups.add(r.frameGroupId);
      });
      this.allUniqueStores = Array.from(stores).sort();
      this.allUniqueDevices = Array.from(devices).sort();
      this.allUniqueFrameGroups = Array.from(frameGroups).sort();

      // Check for user preferences if not loaded yet
      if (this.savedPreferenceFrameGroups === null) {
        const user = this.auth.currentUser;
        if (user) {
          this.savedPreferenceFrameGroups = await this.getUserPreferences(user.uid);
        }
      }

      // Initialize selections if empty
      if (this.selectedStores.size === 0 && this.selectedDevices.size === 0 && this.selectedFrameGroups.size === 0) {
        this.allUniqueStores.forEach(s => this.selectedStores.add(s));
        this.allUniqueDevices.forEach(d => this.selectedDevices.add(d));
        
        if (this.savedPreferenceFrameGroups && this.savedPreferenceFrameGroups.length > 0) {
          const validGroups = this.savedPreferenceFrameGroups.filter(g => frameGroups.has(g));
          if (validGroups.length > 0) {
            validGroups.forEach(g => this.selectedFrameGroups.add(g));
          } else {
            this.allUniqueFrameGroups.forEach(g => this.selectedFrameGroups.add(g));
          }
        } else {
          this.allUniqueFrameGroups.forEach(g => this.selectedFrameGroups.add(g));
        }
      } else {
        // Remove stores/devices/groups no longer in list
        const newStoreSet = new Set<string>();
        this.selectedStores.forEach(s => {
          if (stores.has(s)) newStoreSet.add(s);
        });
        this.selectedStores = newStoreSet;

        const newDeviceSet = new Set<string>();
        this.selectedDevices.forEach(s => {
          if (devices.has(s)) newDeviceSet.add(s);
        });
        this.selectedDevices = newDeviceSet;

        const newGroupSet = new Set<string>();
        this.selectedFrameGroups.forEach(g => {
          if (frameGroups.has(g)) newGroupSet.add(g);
        });
        this.selectedFrameGroups = newGroupSet;
      }

      this.applyFiltersAndPagination();
      
    } finally {
      this.zone.run(() => {
        this.isLoading = false;
      });
    }
  }

  applyFiltersAndPagination() {
    let filtered = [...this.baseAggregatedData];
    
    // Frame Group ID Filter
    if (this.selectedFrameGroups.size > 0 && this.selectedFrameGroups.size < this.allUniqueFrameGroups.length) {
      filtered = filtered.filter(row => row.frameGroupId && this.selectedFrameGroups.has(row.frameGroupId));
    } else if (this.selectedFrameGroups.size === 0 && this.allUniqueFrameGroups.length > 0) {
      filtered = [];
    }

    if (this.reportType === 'store') {
      filtered = filtered.filter(row => row.storeId && this.selectedStores.has(row.storeId));
    } else if (this.reportType === 'device') {
      filtered = filtered.filter(row => 
        row.storeId && this.selectedStores.has(row.storeId) && 
        row.deviceId && this.selectedDevices.has(row.deviceId)
      );
    }

    // Re-rank dynamically based on the filtered results
    filtered.sort((a, b) => b.printCount - a.printCount);
    filtered.forEach((row, index) => {
      row.rank = index + 1;
    });

    this.gridData = filtered;
    this.currentPage = 1;
    this.updatePage();
  }

  async openPdfPreview() {
    this.isGeneratingPdf = true;
    this.loadingMessage = 'Preparing PDF Preview...';
    try {
      // Pre-fetch all images for the filtered dataset so preview and PDF have all images rendered
      await this.populateImages(this.gridData);
      this.currentDate = new Date();
      this.isPreviewModalOpen = true;
    } catch (error) {
      console.error('Error preparing frame report preview:', error);
      alert('Failed to prepare preview.');
    } finally {
      this.isGeneratingPdf = false;
    }
  }

  closePdfPreview() {
    this.isPreviewModalOpen = false;
  }

  async printPreviewReport() {
    this.isExporting = true;
    this.loadingMessage = 'Generating PDF for print...';
    let pdfWindow: Window | null = null;
    try {
      pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write('<html><body><div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;"><div>Generating PDF... Please wait.</div></div></body></html>');
      }
    } catch (e) {
      console.warn('Could not open pre-window for PDF', e);
    }

    try {
      // Pre-fetch all images
      await this.populateImages(this.gridData);
      this.cd.detectChanges();
      await new Promise(resolve => setTimeout(resolve, 500));

      const element = document.getElementById('frame-report-pdf-content');
      if (!element) {
        throw new Error('PDF element not found');
      }

      const month = this.selectedDate.getMonth() + 1;
      const year = this.selectedDate.getFullYear();
      const periodStr = `${year}-${String(month).padStart(2, '0')}`;
      const filename = `Frame_Incentive_Report_${periodStr}.pdf`;

      await this.pdfService.openPdf(element, filename, 'landscape', pdfWindow || undefined);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to print PDF.');
      if (pdfWindow) pdfWindow.close();
    } finally {
      this.isExporting = false;
      this.cd.detectChanges();
    }
  }

  async savePreviewReport() {
    this.isExporting = true;
    this.loadingMessage = 'Generating and saving PDF...';
    try {
      await this.populateImages(this.gridData);
      this.cd.detectChanges();
      await new Promise(resolve => setTimeout(resolve, 500));

      const element = document.getElementById('frame-report-pdf-content');
      if (!element) {
        throw new Error('PDF element not found');
      }

      const month = this.selectedDate.getMonth() + 1;
      const year = this.selectedDate.getFullYear();
      const periodStr = `${year}-${String(month).padStart(2, '0')}`;
      const filename = `Frame_Incentive_Report_${periodStr}.pdf`;

      await this.pdfService.savePdf(element, filename, 'landscape');
    } catch (error) {
      console.error('Error saving PDF:', error);
      alert('Failed to save PDF.');
    } finally {
      this.isExporting = false;
      this.cd.detectChanges();
    }
  }

  async exportPdf() {
    await this.openPdfPreview();
  }

  async populateImages(rows: AggregatedFrameRow[]) {
    const toFetch = rows;
    
    const promises = toFetch.map(async (row) => {
      if (row.imageUrl) return; // already fetched

      const cacheKey = `${row.frameGroupId}_${row.frameId}`;
      if (this.imageUrlCache.has(cacheKey)) {
        row.imageUrl = this.imageUrlCache.get(cacheKey);
        return;
      }

      // Try to get exact URL via photobooth service
      try {
        // Fallback to "Unknown" deviceId or themeId if doing overall aggregation, 
        // the service usually looks up by themeId/frameGroupId/frameId in global assets if deviceId is invalid.
        const devId = row.deviceId || 'ALL'; 
        const result = await this.photoboothService.resolveFrameImageUrl(devId, row.themeId, row.frameGroupId, row.frameId);
        
        if (result.url) {
          row.imageUrl = result.url;
          this.imageUrlCache.set(cacheKey, result.url);
        }
        row.debugPath = result.debugPath;
      } catch (e) {
        this.imageUrlCache.set(cacheKey, null);
      }
    });

    await Promise.all(promises);
  }

  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [10, 20, 50, 100];
  paginatedGridData: AggregatedFrameRow[] = [];

  get totalPages() {
    return Math.ceil(this.gridData.length / this.pageSize) || 1;
  }

  async onPageSizeChange() {
    this.currentPage = 1;
    await this.updatePage();
  }

  async updatePage() {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    this.paginatedGridData = this.gridData.slice(startIndex, startIndex + this.pageSize);
    await this.populateImages(this.paginatedGridData);
  }

  async nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      await this.updatePage();
    }
  }

  async prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      await this.updatePage();
    }
  }

  previewFrame: AggregatedFrameRow | null = null;

  openPreview(frame: AggregatedFrameRow) {
    if (frame.imageUrl) {
      this.previewFrame = frame;
    }
  }

  closePreview() {
    this.previewFrame = null;
  }
}
